import { useNavigate } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import { FolderKanban } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getNavForRole, type PbosRole } from "@/components/layout/nav-items";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useProjects } from "@/hooks/use-projects";

interface ProjectSearchPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: PbosRole;
}

interface PageEntry {
  label: string;
  breadcrumb: string;
  path: string;
  icon: LucideIcon;
}

function buildPages(role: PbosRole): PageEntry[] {
  const items = getNavForRole(role);
  const pages: PageEntry[] = [];
  for (const item of items) {
    if (item.path) {
      pages.push({ label: item.label, breadcrumb: item.label, path: item.path, icon: item.icon });
    }
    if (item.children) {
      for (const child of item.children) {
        pages.push({
          label: child.label,
          breadcrumb: `${item.label} › ${child.label}`,
          path: child.path,
          icon: child.icon,
        });
      }
    }
  }
  return pages;
}

export function ProjectSearchPalette({ open, onOpenChange, role }: ProjectSearchPaletteProps) {
  const { data: projects, isLoading } = useProjects();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const pages = useMemo(() => buildPages(role), [role]);

  const pageResults = useMemo(() => {
    if (!search.trim()) return pages;
    const q = search.toLowerCase();
    return pages.filter(
      (p) => p.label.toLowerCase().includes(q) || p.breadcrumb.toLowerCase().includes(q),
    );
  }, [pages, search]);

  const projectResults = useMemo(() => {
    if (!projects) return [];
    if (!search.trim()) return projects.slice(0, 20);
    const q = search.toLowerCase();
    return projects
      .filter((p) => {
        const name = p.name?.toLowerCase() ?? "";
        const num = p.projectNumber?.toLowerCase() ?? "";
        const client = p.client?.toLowerCase() ?? "";
        return name.includes(q) || num.includes(q) || client.includes(q);
      })
      .slice(0, 25);
  }, [projects, search]);

  function handleSelectProject(id: string) {
    onOpenChange(false);
    navigate({
      to: "/projects/$projectId",
      params: { projectId: id },
      search: { tab: "overview" },
    });
  }

  function handleSelectPage(path: string) {
    onOpenChange(false);
    navigate({ to: path });
  }

  const nothingFound =
    !isLoading && pageResults.length === 0 && projectResults.length === 0 && search.trim() !== "";

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search projects and menu"
      description="Find a project or jump to a page"
    >
      <CommandInput
        value={search}
        onValueChange={setSearch}
        placeholder="Search projects and menu…"
      />
      <CommandList>
        {nothingFound && <CommandEmpty>No matches for "{search}".</CommandEmpty>}
        {pageResults.length > 0 && (
          <CommandGroup heading="Pages">
            {pageResults.map((p) => {
              const Icon = p.icon;
              return (
                <CommandItem
                  key={p.path}
                  value={`page:${p.breadcrumb}`}
                  onSelect={() => handleSelectPage(p.path)}
                >
                  <Icon className="h-4 w-4" />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm">{p.label}</span>
                    {p.breadcrumb !== p.label && (
                      <span className="truncate text-[11px] text-muted-foreground">
                        {p.breadcrumb}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">Loading projects…</div>
        ) : (
          projectResults.length > 0 && (
            <CommandGroup heading="Projects">
              {projectResults.map((p) => {
                const subtitle = [p.projectNumber, p.client].filter(Boolean).join(" · ");
                return (
                  <CommandItem
                    key={p.id}
                    value={`project:${p.name} ${p.projectNumber ?? ""} ${p.client ?? ""}`}
                    onSelect={() => handleSelectProject(p.id)}
                  >
                    <FolderKanban className="h-4 w-4" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm">{p.name}</span>
                      {subtitle && (
                        <span className="truncate text-[11px] text-muted-foreground">
                          {subtitle}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )
        )}
      </CommandList>
    </CommandDialog>
  );
}
